import { z } from "zod";
import { OperationSchema } from "./operations";
import { AssetSchema } from "./assets";
import { CurrencyCodeSchema } from "./currency";
import { HydratedOrganizationFileSchema } from "./organizationFiles";
import {
  CustomFieldValuesSchema,
  CustomFieldsSchemaSchema,
} from "./customFields";
import { ExternalMappingSchema } from "./externalMappings";
import { PrincipalActorSchema, PrincipalKindSchema } from "./principal";
import { SettingsLockedBySchema } from "../erpIntegrations/settingsLocks";

// Enums
export const ItemTypeSchema = z.enum(["MANUFACTURED", "PURCHASED", "MATERIAL"]);
export type ItemType = z.infer<typeof ItemTypeSchema>;
export const ItemTypeEnum = ItemTypeSchema.enum;

/** Display labels for item types. */
export const ItemTypeLabel: Record<ItemType, string> = {
  MANUFACTURED: "Manufactured",
  PURCHASED: "Purchased",
  MATERIAL: "Material",
};

export const CostingMethodSchema = z.enum(["BOM", "UNIT_COST"]);
export type CostingMethod = z.infer<typeof CostingMethodSchema>;
export const CostingMethodEnum = CostingMethodSchema.enum;

/** Display labels for costing methods. */
export const CostingMethodLabel: Record<CostingMethod, string> = {
  BOM: "BOM",
  UNIT_COST: "Unit Cost",
};

/**
 * Derives the default costing method from an item type.
 * MANUFACTURED defaults to BOM, PURCHASED and MATERIAL default to UNIT_COST.
 * @param itemType - The item type to derive from
 * @returns The default costing method
 */
export function getDefaultCostingMethod(itemType: ItemType): CostingMethod {
  if (itemType === ItemTypeEnum.MANUFACTURED) {
    return CostingMethodEnum.BOM;
  }
  return CostingMethodEnum.UNIT_COST;
}

/**
 * Default guidance for how the agent should name items it creates or updates.
 * Wired into the item creation/update tools (see `buildItemNamingGuidanceHint`)
 * so item naming is consistent across customers unless a customer-specific SOP
 * overrides it.
 *
 * Convention: the item name is the part (or drawing) number and nothing else;
 * the drawing title/description goes in the description field, the revision goes
 * in the revision field, and tooling is suffixed with " - Tooling".
 */
export const DEFAULT_ITEM_NAMING_GUIDANCE = [
  "The item name should be the part number (or drawing number) and nothing else — do not add the customer name, material, alloy, revision, or a description to the name.",
  "The item description should be the drawing name / title / description of the part.",
  "Put the revision in the revision field, never in the name.",
  "For tooling, name the item '<part number> - Tooling' unless otherwise specified.",
  "If no part number is available, use a short, specific description of the part as the name.",
].join("\n");

/** Item units available before Epicor primitive support is enabled. */
export const LegacyItemUnitSchema = z.enum([
  "kg",
  "pounds",
  "centimeters",
  "meters",
  "feet",
  "inches",
  "each",
  "MLF",
]);

export const ItemUnitSchema = z.enum([
  "bags",
  "boxes",
  "cases",
  "cylinders",
  "drums",
  "gallons",
  "kg",
  "liters",
  "lots",
  "pounds",
  "pails",
  "packages",
  "centimeters",
  "meters",
  "feet",
  "inches",
  "rolls",
  "sets",
  "tons",
  "totes",
  "each",
  "MLF",
]);
export type ItemUnit = z.infer<typeof ItemUnitSchema>;
export const ItemUnitEnum = ItemUnitSchema.enum;

/** Maps an ItemUnit value to its abbreviated form (e.g. "kg", "lb", "ft"). */
export const ITEM_UNIT_ABBREVIATION: Record<ItemUnit, string> = {
  [ItemUnitEnum.bags]: "bag",
  [ItemUnitEnum.boxes]: "box",
  [ItemUnitEnum.cases]: "case",
  [ItemUnitEnum.cylinders]: "cyl",
  [ItemUnitEnum.drums]: "drum",
  [ItemUnitEnum.gallons]: "gal",
  [ItemUnitEnum.each]: "ea",
  [ItemUnitEnum.kg]: "kg",
  [ItemUnitEnum.liters]: "L",
  [ItemUnitEnum.lots]: "lot",
  [ItemUnitEnum.pounds]: "lb",
  [ItemUnitEnum.pails]: "pail",
  [ItemUnitEnum.packages]: "pkg",
  [ItemUnitEnum.meters]: "m",
  [ItemUnitEnum.centimeters]: "cm",
  [ItemUnitEnum.feet]: "ft",
  [ItemUnitEnum.inches]: "in",
  [ItemUnitEnum.rolls]: "roll",
  [ItemUnitEnum.sets]: "set",
  [ItemUnitEnum.tons]: "ton",
  [ItemUnitEnum.totes]: "tote",
  [ItemUnitEnum.MLF]: "MLF",
};

/** Maps an ItemUnit value to its human-readable display label. */
export const ITEM_UNIT_LABEL: Record<ItemUnit, string> = {
  [ItemUnitEnum.bags]: "Bags",
  [ItemUnitEnum.boxes]: "Boxes",
  [ItemUnitEnum.cases]: "Cases",
  [ItemUnitEnum.cylinders]: "Cylinders",
  [ItemUnitEnum.drums]: "Drums",
  [ItemUnitEnum.gallons]: "Gallons",
  [ItemUnitEnum.each]: "Each",
  [ItemUnitEnum.kg]: "Kilograms (kg)",
  [ItemUnitEnum.liters]: "Liters",
  [ItemUnitEnum.lots]: "Lots",
  [ItemUnitEnum.pounds]: "Pounds",
  [ItemUnitEnum.pails]: "Pails",
  [ItemUnitEnum.packages]: "Packages",
  [ItemUnitEnum.meters]: "Meters",
  [ItemUnitEnum.centimeters]: "Centimeters",
  [ItemUnitEnum.feet]: "Feet",
  [ItemUnitEnum.inches]: "Inches",
  [ItemUnitEnum.rolls]: "Rolls",
  [ItemUnitEnum.sets]: "Sets",
  [ItemUnitEnum.tons]: "Tons",
  [ItemUnitEnum.totes]: "Totes",
  [ItemUnitEnum.MLF]: "MLF",
};

export const WeightUnitSchema = z.enum(["kg", "pounds"]);
/** Unit of measure for item weight (mass only). */
export type WeightUnit = z.infer<typeof WeightUnitSchema>;
export const WeightUnitEnum = WeightUnitSchema.enum;

/** Maps a WeightUnit value to its abbreviated form (e.g. "kg", "lb"). */
export const WEIGHT_UNIT_ABBREVIATION: Record<WeightUnit, string> = {
  [WeightUnitEnum.kg]: "kg",
  [WeightUnitEnum.pounds]: "lb",
};

export const DimensionUnitSchema = z.enum([
  "meters",
  "centimeters",
  "feet",
  "inches",
  "MLF",
]);
/** Unit of measure for item dimensions (length, width, height). */
export type DimensionUnit = z.infer<typeof DimensionUnitSchema>;
export const DimensionUnitEnum = DimensionUnitSchema.enum;

/** Maps a DimensionUnit value to its abbreviated form (e.g. "m", "ft"). */
export const DIMENSION_UNIT_ABBREVIATION: Record<DimensionUnit, string> = {
  [DimensionUnitEnum.meters]: "m",
  [DimensionUnitEnum.centimeters]: "cm",
  [DimensionUnitEnum.feet]: "ft",
  [DimensionUnitEnum.inches]: "in",
  [DimensionUnitEnum.MLF]: "MLF",
};

/** Maps a DimensionUnit to the mark printed after a measurement (e.g. 24″, 10′). */
export const DIMENSION_UNIT_SYMBOL: Record<DimensionUnit, string> = {
  ...DIMENSION_UNIT_ABBREVIATION,
  [DimensionUnitEnum.feet]: "′",
  [DimensionUnitEnum.inches]: "″",
};

/** Maps an AreaUnit value to its abbreviated form (e.g. "sq ft", "m²"). */
export const AREA_UNIT_ABBREVIATION: Record<AreaUnit, string> = {
  square_feet: "sq ft",
  square_inches: "sq in",
  square_meters: "m²",
};

export const AreaUnitSchema = z.enum([
  "square_feet",
  "square_inches",
  "square_meters",
]);
/** Unit of measure for area. Used by pricing units and conversions, not by items. */
export type AreaUnit = z.infer<typeof AreaUnitSchema>;
export const AreaUnitEnum = AreaUnitSchema.enum;

export const ConversionUnitSchema = z.enum([
  ...ItemUnitSchema.options,
  ...AreaUnitSchema.options,
]);
/** Any unit the unit conversion engine can convert between. */
export type ConversionUnit = z.infer<typeof ConversionUnitSchema>;

/** Maps a ConversionUnit value to its abbreviated form (e.g. "ea", "lb", "sq ft"). */
export const CONVERSION_UNIT_ABBREVIATION: Record<ConversionUnit, string> = {
  ...ITEM_UNIT_ABBREVIATION,
  ...AREA_UNIT_ABBREVIATION,
};

/** Maps a DimensionUnit value to its human-readable display label. */
export const DIMENSION_UNIT_LABEL: Record<DimensionUnit, string> = {
  [DimensionUnitEnum.meters]: "Meters (m)",
  [DimensionUnitEnum.centimeters]: "Centimeters (cm)",
  [DimensionUnitEnum.feet]: "Feet (ft)",
  [DimensionUnitEnum.inches]: "Inches (in)",
  [DimensionUnitEnum.MLF]: "MLF",
};

/**
 * Margin types for items:
 * - "all": Works for ALL item types. For BOM items, marks up all BOM cost categories. For UNIT_COST items, marks up the item's cost breaks.
 * - "purchased", "operations", "outsideOperations", "material", "manufactured": Work ONLY for BOM items. Mark up specific BOM cost categories.
 */
/** Review status for items: tracks whether an agent-modified item has been reviewed by a user. */
export const ItemReviewStatusSchema = z.enum(["NEEDS_REVIEW", "REVIEWED"]);
export type ItemReviewStatus = z.infer<typeof ItemReviewStatusSchema>;

export const MarginTypeSchema = z.enum([
  "purchased",
  "operations",
  "outsideOperations",
  "material",
  "manufactured",
  "all",
]);
export type MarginType = z.infer<typeof MarginTypeSchema>;
export const MarginTypeEnum = MarginTypeSchema.enum;

// Quantity break schema for per-quantity-dependent operation cost overrides
export const OperationQuantityBreakSchema = z.object({
  minQty: z.number().int().positive(),
  overrideOperationCost: z.string(),
});
export type OperationQuantityBreak = z.infer<
  typeof OperationQuantityBreakSchema
>;

export const RunTimeFormatSchema = z.enum([
  "PIECES_PER_HOUR",
  "HOURS_PER_PIECE",
  "MINUTES_PER_PIECE",
]);
export type RunTimeFormat = z.infer<typeof RunTimeFormatSchema>;

/** Maximum length of a sequence number, matching the database column. */
export const SEQUENCE_NUMBER_MAX_LENGTH = 50;

// Base schemas for nested objects
export const ItemOperationSchema = z.object({
  id: z.string().optional(),
  operationId: z.string(),
  description: z.string().nullable().optional(),
  sequenceNumber: z
    .string()
    .max(SEQUENCE_NUMBER_MAX_LENGTH)
    .nullable()
    .optional(),
  setupTime: z.string().optional(),
  runTimePerItem: z.string().optional(),
  qtyPer: z.string().optional(),
  runTimeFormat: RunTimeFormatSchema.nullable().optional(),
  efficiency: z.string().optional().default("100"), // optional and default for backwards compatibility to itemDeepCopy that have no efficiency
  overrideOperationCost: z.string().optional(),
  overrideSetupRate: z.string().optional(),
  minimumCharge: z.string().optional(),
  reasoning: z.string(),
  quantityBreaks: z.array(OperationQuantityBreakSchema).optional(),
});
export type ItemOperation = z.infer<typeof ItemOperationSchema>;

export const ItemComponentSchema = z.object({
  id: z.string().optional(),
  itemId: z.string(),
  description: z.string().nullable().optional(),
  quantity: z.string(),
  fixedQuantity: z.boolean().optional(),
  costingMethod: CostingMethodSchema.nullable().optional(),
  scrapPercent: z.string().optional(),
  overrideComponentCost: z.string().optional(),
  relatedItemOperationId: z.string().nullable().optional(),
  reasoning: z.string(),
});
/** An item used as a component within a parent item's BOM. */
export type ItemComponent = z.infer<typeof ItemComponentSchema>;

export const ItemCostSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  minQty: z.number().int().positive(),
  unitCost: z.string(),
});
export type ItemCost = z.infer<typeof ItemCostSchema>;

export const CreateItemCostSchema = z.object({
  minQty: z.number().int().positive(),
  unitCost: z.string(),
});
export type CreateItemCost = z.infer<typeof CreateItemCostSchema>;

export const ItemUpgradeSchema = z.object({
  id: z.string(),
  type: z.enum(["item", "section"]),
  description: z.string().nullable().optional(),
  itemId: z.string(),
  quantity: z.string(),
  upgradeForItemId: z.string(),
});
export type ItemUpgrade = z.infer<typeof ItemUpgradeSchema>;

export const ItemMarginSchema = z.object({
  type: MarginTypeSchema,
  minQty: z.number().int().positive().optional(),
  marginAmount: z.union([
    z.object({
      type: z.literal("PERCENTAGE"),
      percentage: z.string(),
    }),
    z.object({
      type: z.literal("AMOUNT"),
      amount: z.string(),
    }),
  ]),
});
export type ItemMargin = z.infer<typeof ItemMarginSchema>;

export const ItemYieldSchema = z.object({
  minQty: z.number().int().positive().optional(),
  percentage: z.string(),
});
export type ItemYield = z.infer<typeof ItemYieldSchema>;

export const ItemUnitConversionSchema = z.object({
  fromUnit: ConversionUnitSchema,
  toUnit: ConversionUnitSchema,
  rate: z.string(),
});
/** One `fromUnit` equals `rate` × `toUnit`; conversions use it in both directions. */
export type ItemUnitConversion = z.infer<typeof ItemUnitConversionSchema>;

/** Names of pricing lines must be identifier-safe so later lines can reference them in formulas. */
export const PRICING_LINE_NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/** A quantity-tiered formula override for a pricing line; the highest minQty <= quantity wins. */
export const PricingLineQuantityBreakSchema = z.object({
  minQty: z.number().int().positive(),
  formula: z.string().min(1),
});
export type PricingLineQuantityBreak = z.infer<
  typeof PricingLineQuantityBreakSchema
>;

/**
 * When a pricing line is evaluated relative to margin:
 * - COST lines run before margin and add to the cost basis the margin applies
 *   to (loaded cost, e.g. overhead and efficiency allocations). The last COST
 *   line's value becomes the cost base for an "all" margin.
 * - PRICE lines (the default) run after margin and modify the selling price
 *   (e.g. shipping, commission, surcharges).
 */
export const PricingLinePhaseSchema = z.enum(["COST", "PRICE"]);
export type PricingLinePhase = z.infer<typeof PricingLinePhaseSchema>;

/**
 * One named step in an item's pricing calculation, evaluated in array order
 * within its phase. Formulas are arithmetic expressions over `qty`, `cost.*`
 * unit-cost categories, the names of earlier lines, and — for PRICE-phase
 * lines — `basePrice` (loaded cost plus margins). COST-phase lines run before
 * margin and feed the cost base; PRICE-phase lines run after margin, and the
 * last PRICE line's value becomes the item's unit price.
 */
export const PricingLineSchema = z.object({
  name: z.string().regex(PRICING_LINE_NAME_REGEX),
  description: z.string().optional(),
  formula: z.string().min(1),
  phase: PricingLinePhaseSchema.optional(),
  quantityBreaks: z.array(PricingLineQuantityBreakSchema).optional(),
});
export type PricingLine = z.infer<typeof PricingLineSchema>;

/**
 * A pricing line's computed value at a specific quantity, for breakdown display.
 * `value` is the cumulative running price at this step; `delta` is the signed
 * increment this line adds over the previous step (the phase anchor for the
 * first line). `phase` indicates whether the line ran before margin (COST) or
 * after it (PRICE), so the breakdown can place the margin step correctly. The
 * waterfall reconciles exactly: unitCost + Σ(COST deltas) + margin + Σ(PRICE
 * deltas) = unitPrice.
 */
export const EvaluatedPricingLineSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  value: z.string(),
  delta: z.string(),
  phase: PricingLinePhaseSchema,
});
export type EvaluatedPricingLine = z.infer<typeof EvaluatedPricingLineSchema>;

export const CostAtQuantitySchema = z.object({
  quantity: z.string(),
  unitCost: z.string(),
});
export type CostAtQuantity = z.infer<typeof CostAtQuantitySchema>;

export const PriceAtQuantitySchema = z.object({
  quantity: z.string(),
});
export type PriceAtQuantity = z.infer<typeof PriceAtQuantitySchema>;

// ============================================================================
// PRICE MODEL INPUT METADATA — per-input-leaf provenance
// ============================================================================

/** A `Principal` minus its permissions, safe to persist as provenance. */
export const StoredPrincipalAttributionSchema = z.object({
  kind: PrincipalKindSchema,
  userId: z.string().optional(),
  organizationId: z.string(),
  actor: PrincipalActorSchema.optional(),
});
/** Persisted identity of whoever wrote a price-model input value. */
export type StoredPrincipalAttribution = z.infer<
  typeof StoredPrincipalAttributionSchema
>;

/** Provenance for a single price-model input leaf, addressed by schema path. */
export const PriceModelInputMetadataEntrySchema = z.object({
  schemaPath: z.array(z.string()).min(1),
  reasoning: z.string().optional(),
  source: z.object({
    type: z.literal("PRINCIPAL"),
    principal: StoredPrincipalAttributionSchema,
  }),
  updatedAt: z.coerce.date(),
});
/** Provenance record for one price-model input leaf. */
export type PriceModelInputMetadataEntry = z.infer<
  typeof PriceModelInputMetadataEntrySchema
>;

/**
 * A metadata entry as submitted on a write. Callers supply the leaf and its
 * rationale; the server stamps `source` and `updatedAt` itself, so attribution
 * can never be minted by a client.
 */
export const SubmittedPriceModelInputMetadataEntrySchema =
  PriceModelInputMetadataEntrySchema.pick({
    schemaPath: true,
    reasoning: true,
  });
/** A price-model input metadata entry as supplied by a client or agent. */
export type SubmittedPriceModelInputMetadataEntry = z.infer<
  typeof SubmittedPriceModelInputMetadataEntrySchema
>;

// Core Item schema (API response type)
export const ItemSchema = z.object({
  id: z.string(),
  type: ItemTypeSchema,
  costingMethod: CostingMethodSchema,
  name: z.string(),
  revision: z.string(),
  description: z.string().nullable(),
  // Internal estimator briefing (markdown), agent-authored and user-editable.
  // Optional so legacy quote deep copies (without it) parse.
  briefing: z.string().nullable().optional(),
  primaryFileAssetId: z.string().nullable(),
  fileAssetIds: z.array(z.string()),
  // Vault (organization file) ids linked to this item. Optional because not
  // every read loads the links (batch accessors omit them) and legacy quote
  // deep copies predate the field — undefined means "not loaded", not "none".
  organizationFileIds: z.array(z.string()).optional(),
  operations: z.array(ItemOperationSchema),
  components: z.array(ItemComponentSchema),
  upgrades: z.array(ItemUpgradeSchema),
  unit: ItemUnitSchema,
  weight: z.string().nullable().optional(),
  weightUnit: WeightUnitSchema.nullable().optional(),
  dimensionUnit: DimensionUnitSchema.nullable().optional(),
  length: z.string().nullable().optional(),
  width: z.string().nullable().optional(),
  height: z.string().nullable().optional(),
  allowance: z.string().nullable().optional(),
  costBreaks: z.array(ItemCostSchema).optional(),
  margin: z.array(ItemMarginSchema),
  yield: z.array(ItemYieldSchema).optional(),
  unitConversions: z.array(ItemUnitConversionSchema).optional(),
  pricingLines: z.array(PricingLineSchema).nullable().optional(),
  priceModelId: z.string().nullable().optional(),
  priceModelInputs: z.record(z.string(), z.any()).nullable().optional(),
  priceModelCalculations: z
    .record(z.string(), z.string())
    .nullable()
    .optional(),
  priceModelInputMetadata: z
    .array(PriceModelInputMetadataEntrySchema)
    .nullable()
    .optional(),
  customFields: CustomFieldValuesSchema.optional(),
  externalMappings: z.array(ExternalMappingSchema).optional(),
  quotedQuantities: z.array(z.string()).optional(),
  reviewStatus: ItemReviewStatusSchema.nullable().optional(),
  // The currency the item's costs/prices are denominated in. Currently always
  // the organization's base currency; stamped at hydration so finalized quote
  // deep copies freeze it. Optional so legacy deep copies (without it) parse.
  currency: CurrencyCodeSchema.optional(),
  archivedAt: z.coerce.date().nullable().optional(),
  organizationId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Item = z.infer<typeof ItemSchema>;

const SettingLimitSchema = z.number().int().positive().nullable();

/** Item settings in the advanced settings section; an ERP can lock them. */
export const ItemAdvancedSettingsSchema = z.object({
  maxPartNumberLength: SettingLimitSchema,
  maxPurchaseCostBreaks: SettingLimitSchema,
  allowOperationEfficiency: z.boolean(),
  allowOperationQuantityBreaks: z.boolean(),
  allowOperationQtyPer: z.boolean(),
  allowComponentFixedQuantity: z.boolean(),
  allowComponentScrap: z.boolean(),
  allowComponentCostOverride: z.boolean(),
  allowComponentCostingMethodOverride: z.boolean(),
  allowComponentOperationLinks: z.boolean(),
  allowedItemUnits: z.array(ItemUnitSchema).nullable(),
});
export type ItemAdvancedSettings = z.infer<typeof ItemAdvancedSettingsSchema>;

/** Advanced item settings of an organization that has not changed them: rarely used BOM fields off, nothing else restricted. */
export const DEFAULT_ITEM_ADVANCED_SETTINGS: ItemAdvancedSettings = {
  maxPartNumberLength: null,
  maxPurchaseCostBreaks: null,
  allowOperationEfficiency: true,
  allowOperationQuantityBreaks: true,
  allowOperationQtyPer: false,
  allowComponentFixedQuantity: false,
  allowComponentScrap: false,
  allowComponentCostOverride: false,
  allowComponentCostingMethodOverride: true,
  allowComponentOperationLinks: true,
  allowedItemUnits: null,
};

export const ItemSettingsSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  customFieldsSchema: CustomFieldsSchemaSchema.nullable(),
  enableAllowance: z.boolean(),
  ...ItemAdvancedSettingsSchema.shape,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ItemSettings = z.infer<typeof ItemSettingsSchema>;

export const UpdateItemSettingsParamsSchema = z
  .object({
    customFieldsSchema: CustomFieldsSchemaSchema.nullable(),
    enableAllowance: z.boolean(),
    ...ItemAdvancedSettingsSchema.shape,
  })
  .partial();
export type UpdateItemSettingsParams = z.infer<
  typeof UpdateItemSettingsParamsSchema
>;

export const ItemSettingsResponseSchema = z.object({
  settings: ItemSettingsSchema.nullable(),
  lockedBy: SettingsLockedBySchema,
});
export type ItemSettingsResponse = z.infer<typeof ItemSettingsResponseSchema>;

// API Request schemas
export const CreateItemParamsSchema = z.object({
  type: ItemTypeSchema,
  costingMethod: CostingMethodSchema.optional(),
  name: z.string(),
  revision: z.string(),
  description: z.string().nullable().optional(),
  briefing: z.string().nullable().optional(),
  primaryFileAssetId: z.string().nullable().optional(),
  fileAssetIds: z.array(z.string()).optional(),
  organizationFileIds: z.array(z.string()).optional(),
  operations: z.array(ItemOperationSchema).optional(),
  components: z.array(ItemComponentSchema).optional(),
  upgrades: z.array(ItemUpgradeSchema).optional(),
  unit: ItemUnitSchema,
  weight: z.string().nullable().optional(),
  weightUnit: WeightUnitSchema.nullable().optional(),
  dimensionUnit: DimensionUnitSchema.nullable().optional(),
  length: z.string().nullable().optional(),
  width: z.string().nullable().optional(),
  height: z.string().nullable().optional(),
  allowance: z.string().nullable().optional(),
  costBreaks: z.array(CreateItemCostSchema).optional(),
  margin: z.array(ItemMarginSchema).optional(),
  yield: z.array(ItemYieldSchema).optional(),
  unitConversions: z.array(ItemUnitConversionSchema).optional(),
  pricingLines: z.array(PricingLineSchema).optional(),
  priceModelId: z.string().nullable().optional(),
  priceModelInputs: z.record(z.string(), z.any()).optional(),
  priceModelCalculations: z.record(z.string(), z.string()).optional(),
  priceModelInputMetadata: z
    .array(SubmittedPriceModelInputMetadataEntrySchema)
    .optional(),
  customFields: CustomFieldValuesSchema.optional(),
  /** Marks the item NEEDS_REVIEW on create, for stubs made inline from the item selector. */
  needsReview: z.boolean().optional(),
});
export type CreateItemParams = z.infer<typeof CreateItemParamsSchema>;

export const UpdateItemParamsSchema = z.object({
  type: ItemTypeSchema.optional(),
  costingMethod: CostingMethodSchema.optional(),
  name: z.string().optional(),
  revision: z.string().optional(),
  description: z.string().nullable().optional(),
  briefing: z.string().nullable().optional(),
  primaryFileAssetId: z.string().nullable().optional(),
  fileAssetIds: z.array(z.string()).optional(),
  organizationFileIds: z.array(z.string()).optional(),
  operations: z.array(ItemOperationSchema).optional(),
  components: z.array(ItemComponentSchema).optional(),
  upgrades: z.array(ItemUpgradeSchema).optional(),
  unit: ItemUnitSchema.optional(),
  weight: z.string().nullable().optional(),
  weightUnit: WeightUnitSchema.nullable().optional(),
  dimensionUnit: DimensionUnitSchema.nullable().optional(),
  length: z.string().nullable().optional(),
  width: z.string().nullable().optional(),
  height: z.string().nullable().optional(),
  allowance: z.string().nullable().optional(),
  costBreaks: z.array(CreateItemCostSchema).optional(),
  margin: z.array(ItemMarginSchema).optional(),
  yield: z.array(ItemYieldSchema).optional(),
  unitConversions: z.array(ItemUnitConversionSchema).optional(),
  pricingLines: z.array(PricingLineSchema).optional(),
  priceModelId: z.string().nullable().optional(),
  priceModelInputs: z.record(z.string(), z.any()).optional(),
  priceModelCalculations: z.record(z.string(), z.string()).optional(),
  priceModelInputMetadata: z
    .array(SubmittedPriceModelInputMetadataEntrySchema)
    .optional(),
  customFields: CustomFieldValuesSchema.optional(),
  quotedQuantities: z.array(z.string()).optional(),
  archived: z.boolean().optional(),
  reviewStatus: z.null().optional(),
});
export type UpdateItemParams = z.infer<typeof UpdateItemParamsSchema>;

export const CalculateCostsParamsSchema = z.object({
  quantities: z.array(z.string()),
  costingMethod: CostingMethodSchema.optional(),
});
export type CalculateCostsParams = z.infer<typeof CalculateCostsParamsSchema>;

// Filter for searching items
export const ItemFilterSchema = z.object({
  search: z.string().optional(),
  type: ItemTypeSchema.optional(),
  costingMethod: CostingMethodSchema.optional(),
  includeArchived: z.boolean().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
});

export type ItemFilter = z.infer<typeof ItemFilterSchema>;

// API Response schemas
export const CreateItemResponseSchema = z.object({
  item: ItemSchema,
});
export type CreateItemResponse = z.infer<typeof CreateItemResponseSchema>;

export const UpdateItemResponseSchema = z.object({
  item: ItemSchema,
});
export type UpdateItemResponse = z.infer<typeof UpdateItemResponseSchema>;

export const GetItemResponseSchema = z.object({
  item: ItemSchema,
});
export type GetItemResponse = z.infer<typeof GetItemResponseSchema>;

export const ListItemsResponseSchema = z.object({
  items: z.array(ItemSchema),
  total: z.number(),
});
export type ListItemsResponse = z.infer<typeof ListItemsResponseSchema>;

export const DeleteItemResponseSchema = z.object({
  success: z.literal(true),
});
export type DeleteItemResponse = z.infer<typeof DeleteItemResponseSchema>;

export const CalculateCostsResponseSchema = z.object({
  costs: z.array(
    z.object({
      quantity: z.string(),
      quantityToMake: z.string().optional(),
      unitCost: z.string(),
      unitPrice: z.string(),
      totalCost: z.string(),
      totalPrice: z.string(),
      marginsByType: z
        .object({
          operations: z.string().optional(),
          outsideOperations: z.string().optional(),
          purchased: z.string().optional(),
          material: z.string().optional(),
          manufactured: z.string().optional(),
          all: z.string().optional(),
        })
        .optional(),
      pricingLines: z.array(EvaluatedPricingLineSchema).optional(),
    }),
  ),
});
export type CalculateCostsResponse = z.infer<
  typeof CalculateCostsResponseSchema
>;

// Error response schema (common to all endpoints)
export const ErrorResponseSchema = z.object({
  error: z.string(),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// Get item query parameters
export const GetItemQuerySchema = z.object({
  nested: z.boolean().optional(),
});
export type GetItemQuery = z.infer<typeof GetItemQuerySchema>;

// Route parameter schemas
export const ItemParamsSchema = z.object({
  id: z.string(),
});
export type ItemParams = z.infer<typeof ItemParamsSchema>;

export const HydratedOperationSchema = ItemOperationSchema.extend({
  operation: OperationSchema,
});
export type HydratedOperation = z.infer<typeof HydratedOperationSchema>;

export type HydratedItem = Omit<
  z.infer<typeof ItemSchema>,
  "components" | "upgrades" | "operations"
> & {
  operations: HydratedOperation[];
  components: HydratedComponent[];
  upgrades: HydratedUpgrade[];
  primaryFileAsset?: z.infer<typeof AssetSchema> | null;
  fileAssets?: z.infer<typeof AssetSchema>[];
  vaultFiles?: z.infer<typeof HydratedOrganizationFileSchema>[];
};

export type HydratedComponent = z.infer<typeof ItemComponentSchema> & {
  component: HydratedItem;
};

export type HydratedUpgrade = z.infer<typeof ItemUpgradeSchema> & {
  upgrade: HydratedItem;
};

export const HydratedItemSchema: z.ZodType<HydratedItem> = ItemSchema.omit({
  components: true,
  upgrades: true,
  operations: true,
}).extend({
  operations: z.array(HydratedOperationSchema),
  components: z.lazy(() =>
    z.array(
      ItemComponentSchema.extend({
        component: HydratedItemSchema,
      }),
    ),
  ),
  upgrades: z.lazy(() =>
    z.array(
      ItemUpgradeSchema.extend({
        upgrade: HydratedItemSchema,
      }),
    ),
  ),
  primaryFileAsset: AssetSchema.nullable().optional(),
  fileAssets: z.array(AssetSchema).optional(),
  vaultFiles: z.array(HydratedOrganizationFileSchema).optional(),
});

export const GetHydratedItemResponseSchema = z.object({
  item: HydratedItemSchema,
});
export type GetHydratedItemResponse = z.infer<
  typeof GetHydratedItemResponseSchema
>;
